// src/services/authService.ts
// Production-ready authentication service matching BEST_PRACTICES.md Section 6.
import { apiClient, syncCsrfToken, setCsrfToken } from '@/api/apiClient'

export interface UserProfile {
  id: number
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  role: string
  institutionId?: number
  classStandard?: string
  studentId?: string
  idCardImageKey?: string
}

export interface ProfileUpdateRequest {
  firstName?: string
  lastName?: string
  phoneNumber?: string | number
  idCardImageTempKey?: string
}

export const authService = {
  /**
   * Initializes application security context on boot.
   */
  async initialize(): Promise<UserProfile | null> {
    await syncCsrfToken()
    try {
      const response = await apiClient.get('/api/v1/auth/me')
      return response.data?.data ?? null
    } catch {
      return null
    }
  },

  /**
   * Web Login with automatic cookie handling.
   */
  async login(email: string, password: string): Promise<UserProfile> {
    const response = await apiClient.post('/api/v1/auth/login', { email, password })
    await syncCsrfToken() // Re-sync CSRF token for the authenticated session
    return response.data?.data
  },

  /**
   * Universal Profile Mutation (Student or Admin).
   */
  async updateProfile(payload: ProfileUpdateRequest): Promise<UserProfile> {
    const response = await apiClient.put('/api/v1/auth/profile', payload)
    return response.data?.data
  },

  /**
   * Deterministic Web Logout.
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/logout')
    } catch (err) {
      console.warn('[AUTH] Backend logout failed, proceeding with client purge:', err)
    } finally {
      setCsrfToken(null)
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = '/login'
    }
  },
}
